import { z } from 'zod';
import { MATERIAL_VALUES, LIGHTING_VALUES } from './media-units-create.js';

/**
 * Canonical list of editable cost/price field keys on a media unit.
 * Single source of truth shared by the web form (`buildUpdateDto`) and the DB
 * write path (`updateMediaUnit`) so the null-clears-on-empty behavior stays in
 * sync — adding a field here (and to the DTO below) wires it everywhere.
 */
export const MEDIA_UNIT_COST_FIELD_KEYS = [
  'catalogPrice',
  'rent',
  'productionCost',
  'installCost',
  'dismantlingCost',
  'exposureCost',
  'additionalCosts',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
] as const;

export type MediaUnitCostFieldKey = (typeof MEDIA_UNIT_COST_FIELD_KEYS)[number];

// ─── UpdateMediaUnitDto ────────────────────────────────────────────────────────

/**
 * All fields from CreateMediaUnitDto but optional (partial update).
 * Adds deletePhotoIds for photo removal.
 * Cost fields remain non-negative when provided.
 */
export const UpdateMediaUnitDto = z.object({
  // Supplier/format references
  supplierId: z.number().int().positive().optional(),
  formatId: z.number().int().positive().optional(),

  // Required fields (optional for partial update)
  supplierNumber: z.string().min(1).optional(),
  regionCode: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  address: z.string().min(1).optional(),
  // `null` explicitly clears the column (for a dimension-optional mediaType,
  // e.g. cityscroll); omitting the field leaves it unchanged. CU-12472jzd1eb.
  widthM: z.number().positive().nullable().optional(),
  heightM: z.number().positive().nullable().optional(),

  // Optional fields
  countyName: z.string().optional(),
  agglomeration: z.string().optional(),
  material: z.enum(MATERIAL_VALUES).nullable().optional(),
  lighting: z.enum(LIGHTING_VALUES).nullable().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  notes: z.string().optional(),
  wfContactPerson: z.string().optional(),
  locationDescription: z.string().optional(),

  // Cost fields. Non-negative when a value is provided. `null` explicitly clears
  // the stored value (sets the column to NULL); omitting the field leaves it unchanged.
  catalogPrice: z.number().nonnegative().nullable().optional(),
  rent: z.number().nonnegative().nullable().optional(),
  exposureCost: z.number().nonnegative().nullable().optional(),
  productionCost: z.number().nonnegative().nullable().optional(),
  installCost: z.number().nonnegative().nullable().optional(),
  dismantlingCost: z.number().nonnegative().nullable().optional(),
  additionalCosts: z.number().nonnegative().nullable().optional(),

  // Client-facing price fields. Same null-clears / omit-keeps semantics as above.
  discountedPrice: z.number().nonnegative().nullable().optional(),
  lastMinutePrice: z.number().nonnegative().nullable().optional(),
  starterPrice: z.number().nonnegative().nullable().optional(),
  clientProduction: z.number().nonnegative().nullable().optional(),
  clientInstallation: z.number().nonnegative().nullable().optional(),
  clientDismantling: z.number().nonnegative().nullable().optional(),
  clientAdditionalFees: z.number().nonnegative().nullable().optional(),

  // Photo deletion
  deletePhotoIds: z.array(z.number().int()).optional(),
});

export type UpdateMediaUnit = z.infer<typeof UpdateMediaUnitDto>;

// ─── CostHistoryEntrySchema ────────────────────────────────────────────────────

export const CostHistoryEntrySchema = z.object({
  id: z.number(),
  // Internal cost fields — gated by cost.view (nulled via INTERNAL_COST_FIELDS
  // in apps/api/src/common/field-visibility.ts when canViewCosts=false)
  rent: z.number().nullable(),
  /**
   * True when this entry's `rent` before/after values were nulled by the
   * server's field-visibility gate rather than genuinely unchanged
   * (rent-cost-visibility-permission, CU-86cbgnt3z). This schema is a flat
   * per-version row, not a diff — the web renderer computes a delta only
   * when consecutive entries' values differ, so a caller lacking the
   * wildcard `cost.view_rent` grant would otherwise see zero rent-change
   * rows once every entry's `rent` collapses to the same nulled value. This
   * marker lets the renderer force-emit a masked rent row instead.
   * `.default(false)` for the same pre-deploy-response rollback-window
   * reason as `costsWithheld` on MediaUnitListItemSchema.
   */
  rentWithheld: z.boolean().default(false),
  exposureCost: z.number().nullable(),
  // Dormant legacy field: `printCost` was consolidated into `productionCost`
  // (CU-86cbc4mjk) and no longer accepts writes, but historical values stay
  // visible here so past manual edits keep rendering under their old label.
  printCost: z.number().nullable(),
  installCost: z.number().nullable(),
  dismantlingCost: z.number().nullable(),
  productionCost: z.number().nullable(),
  additionalCosts: z.number().nullable(),
  // Client-facing price fields — never gated by cost.view (cost.view gates
  // internal costs, never client prices)
  catalogPrice: z.number().nullable(),
  discountedPrice: z.number().nullable(),
  lastMinutePrice: z.number().nullable(),
  starterPrice: z.number().nullable(),
  clientProduction: z.number().nullable(),
  clientInstallation: z.number().nullable(),
  clientDismantling: z.number().nullable(),
  clientAdditionalFees: z.number().nullable(),
  muralEcoProduction: z.number().nullable(),
  // Metadata
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  createdAt: z.string(),
});

export type CostHistoryEntry = z.infer<typeof CostHistoryEntrySchema>;

export const CostHistoryResponseSchema = z.object({
  data: z.array(CostHistoryEntrySchema),
});

export type CostHistoryResponse = z.infer<typeof CostHistoryResponseSchema>;
